import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import AuthLayout from '../components/AuthLayout'
import { CustomOTPInput } from '../components/CustomOtp'
import { useUserProfile } from '../context/UserProfileContext'
import { AuthService } from '../utils/authService'

enum LoginStep {
  CREDENTIALS = 'CREDENTIALS',
  OTP_VERIFICATION = 'OTP_VERIFICATION',
}

type LoginType = 'email' | 'phone'

// Set after the first successful login on this device so returning users get
// the one-time-code shortcut. First-time visitors are kept on password only
// to avoid confusion about which method to pick.
const RETURNING_USER_KEY = 'britfiler.returningUser'

export default function Login() {
  const [step, setStep] = useState<LoginStep>(LoginStep.CREDENTIALS)
  const [loginType, setLoginType] = useState<LoginType>('email')
  const [isReturningUser, setIsReturningUser] = useState(false)
  // First-time users default to password mode; returning users default to the
  // one-time-code shortcut. Either can be flipped via the toggle (returning
  // users only).
  const [showPassword, setShowPassword] = useState(true)
  const [showPasswordText, setShowPasswordText] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
    password: '',
  })
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const { refetchUser } = useUserProfile()

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message)
      window.history.replaceState({}, document.title)
    }
  }, [location])

  // Hydrate the returning-user pre-fill from localStorage once on mount.
  useEffect(() => {
    const raw = localStorage.getItem(RETURNING_USER_KEY)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as {
        identifier?: string
        loginType?: LoginType
      }
      if (
        !parsed?.identifier ||
        (parsed.loginType !== 'email' && parsed.loginType !== 'phone')
      ) {
        return
      }
      setIsReturningUser(true)
      setLoginType(parsed.loginType)
      setShowPassword(false)
      if (parsed.loginType === 'email') {
        setFormData(prev => ({ ...prev, email: parsed.identifier! }))
      } else {
        setFormData(prev => ({
          ...prev,
          phoneNumber: parsed.identifier!.replace(/^\+44/, ''),
        }))
      }
    } catch {
      // Corrupted value — ignore and fall back to first-time-user flow.
    }
  }, [])

  const getIdentifier = () => {
    return loginType === 'email' ? formData.email : `+44${formData.phoneNumber.replace(/\s/g, '')}`
  }

  const getIdentifierLabel = () => {
    return loginType === 'email' ? 'email' : 'phone'
  }

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (loginType === 'email' && !formData.email) {
      setError('Please enter your email address')
      return
    }

    if (loginType === 'phone' && !formData.phoneNumber) {
      setError('Please enter your phone number')
      return
    }

    if (showPassword && !formData.password) {
      setError('Please enter your password')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const loginPayload: any = {
        type: loginType,
      }

      if (loginType === 'email') {
        loginPayload.email = formData.email
      } else {
        loginPayload.phoneNumber = `+44${formData.phoneNumber.replace(/\s/g, '')}`
      }

      if (showPassword && formData.password) {
        loginPayload.password = formData.password
      }

      const response = await AuthService.login(loginPayload)

      if (response.statusCode === 200 || response.statusCode === 201) {
        localStorage.setItem('loginEmail', formData.email)
        setStep(LoginStep.OTP_VERIFICATION)
        setSuccessMessage(
          response.response.message ||
            `OTP sent to your ${getIdentifierLabel()}`
        )
      } else {
        setError('Login failed. Please check your credentials.')
      }
    } catch (err: any) {
      let errorMessage = 'An error occurred during login'

      if (err.response?.message) {
        errorMessage = err.response.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit code')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const verifyPayload: any = {
        type: loginType,
        otp,
      }

      if (loginType === 'email') {
        verifyPayload.email = formData.email
      } else {
        verifyPayload.phoneNumber = formData.phoneNumber
      }

      const response = await AuthService.loginVerifyOtp(verifyPayload)

      if (response.statusCode === 200) {
        AuthService.setTokens(
          response.response.accessToken,
          response.response.refreshToken
        )
        localStorage.removeItem('loginEmail')
        // Remember this user on the device so the next visit offers the
        // one-time-code shortcut alongside password.
        const identifier =
          loginType === 'email'
            ? formData.email
            : `+44${formData.phoneNumber.replace(/\s/g, '')}`
        localStorage.setItem(
          RETURNING_USER_KEY,
          JSON.stringify({ identifier, loginType }),
        )
        await refetchUser()
        navigate('/dashboard')
      } else {
        setError('OTP verification failed. Please try again.')
      }
    } catch (err: any) {
      let errorMessage = 'An error occurred during OTP verification'

      if (err.response?.message) {
        errorMessage = err.response.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    setIsLoading(true)
    setError('')

    try {
      const loginPayload: any = {
        type: loginType,
      }

      if (loginType === 'email') {
        loginPayload.email = formData.email
      } else {
        loginPayload.phoneNumber = formData.phoneNumber
      }

      if (showPassword && formData.password) {
        loginPayload.password = formData.password
      }

      const response = await AuthService.login(loginPayload)

      if (response.statusCode === 200 || response.statusCode === 201) {
        setSuccessMessage(
          `New OTP sent to your ${getIdentifierLabel()}`
        )
        setOtp('')
      }
    } catch {
      setError('Failed to resend OTP. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToCredentials = () => {
    setStep(LoginStep.CREDENTIALS)
    setOtp('')
    setError('')
    setSuccessMessage('')
  }

  return (
    <AuthLayout>
      <div className='w-full max-w-[400px] mx-auto'>
        <div className='mb-8'>
          <h1 className='text-[36px] font-[700] text-[#181D27] mb-3 leading-[38px] text-start'>
            {step === LoginStep.CREDENTIALS
              ? 'Sign In To Your Account'
              : 'Verify Your Identity'}
          </h1>
          <p className='text-[16px] text-[#667085] text-start font-[400] leading-[24px]'>
            {step === LoginStep.CREDENTIALS
              ? 'Welcome back! Please enter your details.'
              : `Enter the 6-digit code sent to ${getIdentifier()}`}
          </p>
        </div>

        {successMessage && (
          <div className='bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm mb-6'>
            {successMessage}
          </div>
        )}

        {error && (
          <div className='bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm mb-6'>
            {error}
          </div>
        )}

        {step === LoginStep.CREDENTIALS ? (
          <form onSubmit={handleCredentialsSubmit} className='space-y-5'>
            {/* Login type toggle */}
            <div className='flex rounded-[8px] border border-[#D0D5DD] overflow-hidden'>
              <button
                type='button'
                onClick={() => {
                  setLoginType('email')
                  setError('')
                }}
                className={`flex-1 h-[40px] text-[14px] font-[600] transition-colors duration-200 ${
                  loginType === 'email'
                    ? 'bg-[#001B38] text-white'
                    : 'bg-white text-[#344054] hover:bg-gray-50'
                }`}
              >
                Email
              </button>
              <button
                type='button'
                onClick={() => {
                  setLoginType('phone')
                  setError('')
                }}
                className={`flex-1 h-[40px] text-[14px] font-[600] transition-colors duration-200 ${
                  loginType === 'phone'
                    ? 'bg-[#001B38] text-white'
                    : 'bg-white text-[#344054] hover:bg-gray-50'
                }`}
              >
                Phone
              </button>
            </div>

            {/* Email or Phone input */}
            {loginType === 'email' ? (
              <div className='flex flex-col items-start justify-center w-full'>
                <label
                  htmlFor='email'
                  className='block text-[14px] font-medium text-[#344054] mb-[6px]'
                >
                  Email
                </label>
                <div className='relative w-full'>
                  <Mail className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
                  <input
                    id='email'
                    type='email'
                    placeholder='olivia@untitledui.com'
                    value={formData.email}
                    onChange={e =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className='w-full h-[44px] pl-[44px] pr-[14px] border border-[#D0D5DD] rounded-[8px] text-[16px] placeholder:text-[#667085] text-black outline-none bg-white'
                  />
                </div>
              </div>
            ) : (
              <div className='flex flex-col items-start justify-center w-full'>
                <label
                  htmlFor='phoneNumber'
                  className='block text-[14px] font-medium text-[#344054] mb-[6px]'
                >
                  Phone Number
                </label>
                <div className='flex w-full'>
                  <div className='flex items-center gap-1.5 px-3 h-[44px] border border-r-0 border-[#D0D5DD] rounded-l-[8px] bg-[#F9FAFB] shrink-0'>
                    <span className='text-[14px]'>🇬🇧</span>
                    <span className='text-[14px] font-medium text-[#344054]'>+44</span>
                  </div>
                  <input
                    id='phoneNumber'
                    type='tel'
                    placeholder='7700 900000'
                    value={formData.phoneNumber}
                    onChange={e => {
                      const value = e.target.value.replace(/[^\d\s]/g, '')
                      setFormData({ ...formData, phoneNumber: value })
                    }}
                    className='w-full h-[44px] pl-[14px] pr-[14px] border border-[#D0D5DD] rounded-r-[8px] text-[16px] placeholder:text-[#667085] text-black outline-none bg-white'
                  />
                </div>
              </div>
            )}

            {/* Sign-in method toggle — only for returning users on this device */}
            {isReturningUser && (
              <div>
                <label className='block text-[14px] font-medium text-[#344054] mb-[6px] text-start'>
                  Sign in with
                </label>
                <div className='flex rounded-[8px] border border-[#D0D5DD] overflow-hidden'>
                  <button
                    type='button'
                    onClick={() => {
                      setShowPassword(false)
                      setError('')
                    }}
                    className={`flex-1 h-[40px] text-[14px] font-[600] transition-colors duration-200 ${
                      !showPassword
                        ? 'bg-[#001B38] text-white'
                        : 'bg-white text-[#344054] hover:bg-gray-50'
                    }`}
                  >
                    One-time code
                  </button>
                  <button
                    type='button'
                    onClick={() => {
                      setShowPassword(true)
                      setError('')
                    }}
                    className={`flex-1 h-[40px] text-[14px] font-[600] transition-colors duration-200 ${
                      showPassword
                        ? 'bg-[#001B38] text-white'
                        : 'bg-white text-[#344054] hover:bg-gray-50'
                    }`}
                  >
                    Password
                  </button>
                </div>
                <p className='mt-2 text-[12px] text-[#667085] text-start leading-[16px]'>
                  {showPassword
                    ? 'Sign in with your existing password.'
                    : `We'll send a 6-digit code to your ${loginType === 'email' ? 'email' : 'phone'} — no password needed.`}
                </p>
              </div>
            )}

            {/* Password field (conditional) */}
            {showPassword && (
              <div className='flex flex-col items-start justify-center w-full'>
                <label
                  htmlFor='password'
                  className='block text-[14px] font-medium text-[#344054] mb-[6px]'
                >
                  Password
                </label>
                <div className='relative w-full'>
                  <Lock className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
                  <input
                    id='password'
                    type={showPasswordText ? 'text' : 'password'}
                    placeholder='••••••••'
                    value={formData.password}
                    onChange={e =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className='w-full h-[44px] pl-[44px] pr-[44px] border border-[#D0D5DD] rounded-[8px] text-[16px] text-black placeholder:text-[#667085] outline-none bg-white'
                  />
                  <button
                    type='button'
                    onClick={() => setShowPasswordText(prev => !prev)}
                    aria-label={
                      showPasswordText ? 'Hide password' : 'Show password'
                    }
                    className='absolute right-[14px] top-1/2 -translate-y-1/2 text-[#667085] hover:text-[#344054]'
                  >
                    {showPasswordText ? (
                      <EyeOff className='w-5 h-5' />
                    ) : (
                      <Eye className='w-5 h-5' />
                    )}
                  </button>
                </div>
                <div className='flex flex-row w-full justify-end mt-[6px]'>
                  <Link
                    to='/forgot-password'
                    className='text-[16px] text-[#334960] font-[600] hover:text-[#6941C6]'
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
            )}

            <button
              type='submit'
              disabled={isLoading}
              className='w-full h-[44px] bg-[#001B38] cursor-pointer text-white text-[16px] font-semibold rounded-[8px] transition-colors duration-200 mt-6 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>

            <div className='flex flex-row items-center justify-start gap-2'>
              <div className='text-black text-[14px]'>
                {' '}
                Don&apos;t have an account?{' '}
              </div>
              <div className='w-[6px] h-[6px] bg-[#F04438] rounded-full' />
              <Link
                to='/sign-up'
                className='text-[#334960] font-semibold text-[14px] cursor-pointer hover:text-[#6941C6] hover:underline'
              >
                Sign up
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className='space-y-5'>
            <div className='flex flex-col items-center justify-center w-full'>
              <CustomOTPInput
                value={otp}
                onChange={setOtp}
                length={6}
              />
            </div>

            <button
              type='submit'
              disabled={isLoading || otp.length !== 6}
              className='w-full h-[44px] bg-[#001B38] cursor-pointer text-white text-[16px] font-semibold rounded-[8px] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isLoading ? 'Verifying...' : 'Verify & Sign In'}
            </button>

            <div className='text-center space-y-3'>
              <button
                type='button'
                onClick={handleResendOtp}
                disabled={isLoading}
                className='text-[#334960] font-semibold text-[14px] hover:text-[#6941C6] disabled:opacity-50'
              >
                Resend Code
              </button>

              <div className='flex flex-row items-center justify-center gap-2'>
                <button
                  type='button'
                  onClick={handleBackToCredentials}
                  className='text-[#334960] font-semibold text-[14px] hover:text-[#6941C6]'
                >
                  ← Back to Login
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  )
}
